/**
 * SFTP connections: a host and a path resource-gateway polls for files on an organization's
 * behalf.
 */
import { hasDirectoryTraversal } from 'notable-common';
import { z } from 'zod';
/** Whether the files on the path are parsed or passed along whole. */
export const ConnectionSftpDataTypeSchema = z.enum(['StructuredFile', 'UnstructuredFile']);
export const ConnectionSftpDataType = ConnectionSftpDataTypeSchema.enum;
/**
 * A path on the remote host as stored: trimmed, with repeated slashes collapsed and no slash at
 * either end, so that `/inbound/` and `inbound` name the same folder.
 */
export function normalizeConnectionSftpPath(path) {
    return path
        .trim()
        .replace(/\/+/g, '/')
        .replace(/^\/|\/$/g, '');
}
/**
 * A path on the remote host, normalized, which may not climb out of the directory it names. It
 * may be empty: an SFTP connection watches its base path joined to this one, and resource-gateway
 * refuses a connection only when both are empty.
 */
export const ConnectionSftpPathSchema = z
    .string()
    .transform(normalizeConnectionSftpPath)
    .refine((value) => !hasDirectoryTraversal(value), {
    message: 'path must not contain path traversal sequences',
});
/** Whether a connection's settings are in use. */
export var ConnectionSettingsState;
(function (ConnectionSettingsState) {
    ConnectionSettingsState["Active"] = "Active";
    ConnectionSettingsState["Inactive"] = "Inactive";
    ConnectionSettingsState["SoftDeleted"] = "SoftDeleted";
})(ConnectionSettingsState || (ConnectionSettingsState = {}));
/** Body `POST /connections/sftp` accepts. */
export const ConnectionSftpCreateBodySchema = z.object({
    organizationId: z.uuid().describe('Organization the connection belongs to.'),
    name: z.string(),
    description: z.string().optional(),
    basePath: ConnectionSftpPathSchema.default('').describe('Folder on the host every path sits under.'),
    path: ConnectionSftpPathSchema.describe('Directory under the base path to poll.'),
    documentType: z.string().trim().min(1).describe('What the files are.').optional(),
    dataType: ConnectionSftpDataTypeSchema,
    host: z.string().trim().min(1),
});
/**
 * Body `PATCH /connections/sftp/{id}` accepts.
 *
 * The state is only the two a caller may choose between: deleting a connection is its own
 * endpoint, so `SoftDeleted` cannot be reached by an update.
 */
export const ConnectionSftpUpdateBodySchema = z.object({
    name: z.string().trim().min(1).optional(),
    description: z.string().nullable().optional(),
    dataType: ConnectionSftpDataTypeSchema.optional(),
    basePath: ConnectionSftpPathSchema.optional(),
    path: ConnectionSftpPathSchema.optional(),
    documentType: z.string().trim().min(1).nullable().optional(),
    host: z.string().trim().min(1).optional(),
    state: z.enum([ConnectionSettingsState.Active, ConnectionSettingsState.Inactive]).optional(),
});
/** Path parameters the endpoints addressing one connection accept. */
export const ConnectionIdParamsSchema = z.object({
    id: z.uuid().describe('The connection.'),
});
//# sourceMappingURL=sftp.js.map